import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState, useCallback, useRef} from 'react';
import {ReactFlow, ReactFlowProvider, Node, ReactFlowInstance} from 'reactflow';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import staticURL from 'lib/staticURL';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {
  StudyInaccessibleContentBanner,
  StudyQueryErrorBanner,
} from 'studies/typed/utils/ui/StudyQueryBanners';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls from 'ui-library/reactflow/controls/HorizontalControls';
import {REACT_FLOW_PRO_OPTIONS} from 'ui-library/reactflow/ReactFlowConstants';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
  sizeToWidth,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar, {FLOWCHART_SIDE_PANEL_WIDTH} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {assertNever} from 'util/Types';

import GroupFlowchartNode, {NodeType as GroupFlowchartNodeType} from './GroupFlowchartNode';
import StudyProcedureRunFlowchartSelectTree from './StudyProcedureRunFlowchartSelectTree';
import {
  makeHoverGroupNodeId,
  makeSelectionGroupNodeId,
  useAutoPanToSelection,
  useFlowchartEdges,
  useFlowchartLayout,
  useFlowchartNodes,
  useProcedureRunLayout,
} from './StudyProcedureRunFlowchartUtils';
import UnitOperationFlowchartNode, {NodeType} from './UnitOperationFlowchartNode';
import {
  getMethodId,
  getOrderedDestPMEIsInColumnsPerProcedureRun,
  UnitOpLinkEdge,
} from './UnitOpLinkingFlowchartUtils';
import useStudyProcedureRunFlowchartState, {
  StudyProcedureRunFlowchartStateStatus,
} from './useStudyProcedureRunFlowchartState';
import {UnitOperationFields} from './useUnitOpLinkingState';

const LEFT_SIDEPANEL_SIZE = SidepanelSize.SMALL;
const LEFT_SIDEPANEL_WIDTH = sizeToWidth[LEFT_SIDEPANEL_SIZE] as number;

const NODE_TYPES = {
  [NodeType.DESTINATION_PMEI]: UnitOperationFlowchartNode,
  [GroupFlowchartNodeType.HOVER_GROUP]: GroupFlowchartNode,
  [GroupFlowchartNodeType.SELECTION_GROUP]: GroupFlowchartNode,
};

const STATIC_PROPS: Partial<React.ComponentProps<typeof ReactFlow>> = {
  nodesConnectable: false,
  nodesDraggable: false,
  panOnScroll: true,
  panOnDrag: true,
  zoomOnScroll: false,
  zoomOnPinch: true,
  defaultViewport: {x: 100, y: 100, zoom: 1},
  minZoom: 0.1,
  proOptions: REACT_FLOW_PRO_OPTIONS,
};

interface StudyProcedureRunFlowchartProps {
  studyId: string;
}

export default function QueryingStudyProcedureRunFlowchart(
  props: StudyProcedureRunFlowchartProps
): JSX.Element {
  const unitOpLinkingState = useStudyProcedureRunFlowchartState(props.studyId);
  if (unitOpLinkingState.status === StudyProcedureRunFlowchartStateStatus.QUERY_ERROR) {
    return <StudyQueryErrorBanner />;
  } else if (unitOpLinkingState.status === StudyProcedureRunFlowchartStateStatus.QUERY_PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <CenteredHorizontalThrobber />
      </>
    );
  } else if (unitOpLinkingState.status === StudyProcedureRunFlowchartStateStatus.STUDY_INACCESSIBLE) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={unitOpLinkingState.inaccessibleStudy}
        modelType={ModelTypeConstants.STUDY}
        displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
      />
    );
  } else if (
    unitOpLinkingState.status ===
    StudyProcedureRunFlowchartStateStatus.CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART
  ) {
    const procedureTypeString = getProcedureRunUIStringForStudyType(unitOpLinkingState.studyType, {
      plural: true,
    });
    return (
      <div className={css(styles.bannerWrapper)}>
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={`This study contains old ${procedureTypeString} that can not be visualized.`}
        />
      </div>
    );
  } else if (unitOpLinkingState.status === StudyProcedureRunFlowchartStateStatus.SUCCESS) {
    return (
      <StudyProcedureRunFlowchart
        unitOperations={unitOpLinkingState.pmeis}
        interRunLinks={unitOpLinkingState.interProcedureLinks}
        studyType={unitOpLinkingState.studyType}
        // Force component remount when PMEIs change after query re-run to ensure ReactFlow state,
        // internal lookup maps, and layout calculations are properly reset with fresh data
        key={unitOpLinkingState.pmeis.map((pmei) => pmei.pmeiId).join(',')}
      />
    );
  } else {
    assert(false, `Unexpected status ${assertNever(unitOpLinkingState.status)}`);
    return <></>;
  }
}

export function StudyProcedureRunFlowchart({
  unitOperations,
  interRunLinks,
  studyType,
}: {
  unitOperations: Array<UnitOperationFields>;
  interRunLinks: Array<UnitOpLinkEdge>;
  studyType: StudyType;
}): JSX.Element {
  return (
    <ReactFlowProvider>
      <ReactFlowWrapper fullWidth={true}>
        <StudyProcedureRunFlowchartPresentational
          unitOperations={unitOperations}
          interRunLinks={interRunLinks}
          studyType={studyType}
        />
      </ReactFlowWrapper>
    </ReactFlowProvider>
  );
}

function StudyProcedureRunFlowchartPresentational({
  unitOperations,
  interRunLinks,
  studyType,
}: {
  unitOperations: Array<UnitOperationFields>;
  interRunLinks: Array<UnitOpLinkEdge>;
  studyType: StudyType;
}): JSX.Element {
  const showEmptyState = useMemo(() => unitOperations.length === 0, [unitOperations]);
  const [isLeftSidepanelOpen, setIsLeftSidepanelOpen] = useState(true);
  const [selectedSidebarIconIndex, setSelectedSidebarIconIndex] = useState<number | null>(0);
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const [hoveringItemId, setHoveringItemId] = useState<string | null>(null);
  const orderedPMEIsInColumnsPerProcedureRun = useMemo(
    () => getOrderedDestPMEIsInColumnsPerProcedureRun(unitOperations),
    [unitOperations]
  );

  // Maps for quick lookup of nodes by different IDs
  const {pmeiIdToUnitOperationFields, methodIdToPMEIs, procedureRunIdToPMEIs} = useMemo(() => {
    const idToUnitOpFields = new Map<string, UnitOperationFields>();
    const idToMethodPMEIs = new Map<string, Array<UnitOperationFields>>();
    const idToProcedureRunPMEIs = new Map<string, Array<UnitOperationFields>>();
    unitOperations.forEach((pmei) => {
      idToUnitOpFields.set(pmei.pmeiId, pmei);
      const methodId = getMethodId(pmei.procedureRunId, pmei.templateMethodId);
      if (!idToMethodPMEIs.has(methodId)) {
        idToMethodPMEIs.set(methodId, []);
      }
      idToMethodPMEIs.get(methodId)!.push(pmei);
      if (!idToProcedureRunPMEIs.has(pmei.procedureRunId)) {
        idToProcedureRunPMEIs.set(pmei.procedureRunId, []);
      }
      idToProcedureRunPMEIs.get(pmei.procedureRunId)!.push(pmei);
    });
    return {
      pmeiIdToUnitOperationFields: idToUnitOpFields,
      methodIdToPMEIs: idToMethodPMEIs,
      procedureRunIdToPMEIs: idToProcedureRunPMEIs,
    };
  }, [unitOperations]);

  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
  const [isReactFlowReady, setIsReactFlowReady] = useState(false);
  useAutoPanToSelection(
    reactFlowInstance,
    isReactFlowReady,
    selectedIds,
    pmeiIdToUnitOperationFields,
    methodIdToPMEIs,
    procedureRunIdToPMEIs
  );

  const makeGroupNodeIdIfKnown = useCallback(
    (makeId: (id: string) => string, hoverOrSelectedId: string): string | null => {
      if (
        pmeiIdToUnitOperationFields.has(hoverOrSelectedId) ||
        methodIdToPMEIs.has(hoverOrSelectedId) ||
        procedureRunIdToPMEIs.has(hoverOrSelectedId)
      ) {
        return makeId(hoverOrSelectedId);
      }
      return null;
    },
    [pmeiIdToUnitOperationFields, methodIdToPMEIs, procedureRunIdToPMEIs]
  );

  const getGroupTitle = useCallback(
    (itemId: string): string => {
      // Check if it's a PMEI
      const pmei = pmeiIdToUnitOperationFields.get(itemId);
      if (pmei) {
        return `${pmei.conditionName} - ${pmei.replicateNumber}`;
      }

      // Check if it's a method group
      const methodPMEIs = methodIdToPMEIs.get(itemId);
      if (methodPMEIs) {
        return methodPMEIs[0].methodName;
      }

      // Check if it's a procedure run
      const procedureRunPMEIs = procedureRunIdToPMEIs.get(itemId);
      if (procedureRunPMEIs) {
        const pmei = procedureRunPMEIs[0];
        return pmei.procedureRunDisplayId
          ? `${pmei.procedureRunName} (${pmei.procedureRunDisplayId})`
          : pmei.procedureRunName || 'Procedure Run';
      }

      return 'Group';
    },
    [pmeiIdToUnitOperationFields, methodIdToPMEIs, procedureRunIdToPMEIs]
  );

  const createGroupNodes = useCallback(
    (hoveringId: string | null, selectedIds: Array<string>): Array<Node> => {
      const groups: Array<Node> = [];

      // Create hover group if hovering over a node that is not selected
      const hoverGroupId =
        hoveringId && !selectedIds.includes(hoveringId)
          ? makeGroupNodeIdIfKnown(makeHoverGroupNodeId, hoveringId)
          : null;
      if (hoverGroupId && hoveringId) {
        groups.push({
          id: hoverGroupId,
          type: GroupFlowchartNodeType.HOVER_GROUP,
          position: {x: 0, y: 0}, // Will be calculated based on child node position
          data: {
            title: getGroupTitle(hoveringId),
            isHover: true,
          },
        });
      }

      // Create selection groups
      for (const selectedId of selectedIds) {
        const selectionGroupId = makeGroupNodeIdIfKnown(makeSelectionGroupNodeId, selectedId);
        if (selectionGroupId) {
          groups.push({
            id: selectionGroupId,
            type: GroupFlowchartNodeType.SELECTION_GROUP,
            position: {x: 0, y: 0},
            data: {
              title: getGroupTitle(selectedId),
              isHover: false,
            },
          });
        }
      }

      return groups;
    },
    [makeGroupNodeIdIfKnown, getGroupTitle]
  );

  const {nodes, setNodes, onNodesChange, nodeLayoutDepsHash} = useFlowchartNodes(unitOperations);
  const edges = useFlowchartEdges(orderedPMEIsInColumnsPerProcedureRun, interRunLinks);

  const {unitOpNodePositionsWithinRuns, procedureRunAbsolutePositions} = useProcedureRunLayout(
    orderedPMEIsInColumnsPerProcedureRun,
    nodes,
    nodeLayoutDepsHash,
    interRunLinks,
    pmeiIdToUnitOperationFields
  );

  useFlowchartLayout(
    setNodes,
    nodeLayoutDepsHash,
    hoveringItemId,
    selectedIds,
    createGroupNodes,
    pmeiIdToUnitOperationFields,
    methodIdToPMEIs,
    procedureRunIdToPMEIs,
    procedureRunAbsolutePositions,
    unitOpNodePositionsWithinRuns
  );

  return (
    <div
      className={css(styles.fullScreenContainer)}
      {...getTestProps({component: 'StudyProcedureRunFlowchart', element: 'root'})}
    >
      <div
        className={css(styles.leftSidepanelContainer)}
        {...getTestProps({component: 'StudyProcedureRunFlowchart', element: 'leftSidepanelContainer'})}
      >
        <FlowchartSidebar
          icons={[
            {
              name: 'multi-select',
              onClick: () => {
                setIsLeftSidepanelOpen(true);
              },
              tooltip: 'Overview',
            },
          ]}
          selectedIconIndex={selectedSidebarIconIndex}
          setSelectedIconIndex={setSelectedSidebarIconIndex}
        />
        <FlowchartContextPanel
          panelSide={PanelSide.LEFT}
          sidepanelName="Overview"
          isOpen={isLeftSidepanelOpen}
          onClose={() => {
            setIsLeftSidepanelOpen(false);
            setSelectedSidebarIconIndex(null);
          }}
          size={SidepanelSize.SMALL}
          showExpandCollapseButtons={false}
        >
          <div
            className={css(styles.selectTreeContainer)}
            {...getTestProps({component: 'StudyProcedureRunFlowchart', element: 'selectTreeContainer'})}
          >
            <StudyProcedureRunFlowchartSelectTree
              studyType={studyType}
              allPMEIs={unitOperations}
              setHoveringItemId={setHoveringItemId}
              selectedIds={selectedIds}
              setSelectedIds={(ids) => {
                if (selectedIds.includes(ids[0])) {
                  setSelectedIds([]);
                } else {
                  setSelectedIds(ids);
                }
              }}
            />
          </div>
        </FlowchartContextPanel>
      </div>
      <div
        style={{
          flexGrow: 1,
          marginLeft: isLeftSidepanelOpen ? LEFT_SIDEPANEL_WIDTH : 0,
          position: 'relative',
        }}
        {...getTestProps({component: 'StudyProcedureRunFlowchart', element: 'flowchart'})}
      >
        <ReactFlow
          nodes={nodes}
          onNodesChange={onNodesChange}
          edges={edges}
          nodeTypes={NODE_TYPES}
          onInit={(instance) => {
            reactFlowInstance.current = instance;
            setIsReactFlowReady(true);
          }}
          {...STATIC_PROPS}
          // Disable interactions when there are no nodes
          panOnDrag={!showEmptyState}
          panOnScroll={!showEmptyState}
          zoomOnPinch={!showEmptyState}
        >
          {!showEmptyState && <HorizontalControls shiftLeft={FLOWCHART_SIDE_PANEL_WIDTH} />}
          <DefaultBackground />
        </ReactFlow>
        {showEmptyState && <StudyProcedureRunFlowchartEmptyView studyType={studyType} />}
      </div>
    </div>
  );
}

export function StudyProcedureRunFlowchartEmptyView({studyType}: {studyType: StudyType}): JSX.Element {
  return (
    <div className={css(styles.emptyStateOverlay)}>
      <img src={staticURL('/static/img/studies/unit-op-instance-linking/EmptyFlowchart.svg')} />
      <div className={css(styles.emptyStateText)}>
        All {getProcedureRunUIStringForStudyType(studyType, {plural: true})} will be visualized here.
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  fullScreenContainer: {
    margin: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
  },
  loadingDiv: {paddingBottom: 5},
  leftSidepanelContainer: {
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    // Ensure left sidepanel renders on top of reactflow canvas
    // Instead of shifting reactflow canvas when left sidepanel is open
    zIndex: 10,
  },
  selectTreeContainer: {height: '100%'},
  bannerWrapper: {
    margin: 8,
    width: '100%',
    display: 'flex',
  },
  emptyStateOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyStateText: {
    ...FONT_PROXIMA_13,
    color: Color.GRAY_700,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    textAlign: 'center',
    marginTop: '16px',
  },
});
